import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { 
  Shield, Clock, Phone, Calendar, CheckCircle, 
  ChevronDown, ChevronUp, Users, Activity,
  Stethoscope, FileText, Heart, ClipboardList,
  UserCheck, Lock, Target, TrendingUp, Dumbbell,
  Search, BarChart3, Briefcase, UserPlus, Award,
  ArrowRight, Star, Sparkles, BadgeCheck
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const GeriatricAdultCare = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // GEWO Adult-Care Color Palette
  const colors = {
    primaryBlue: '#1A4AC5',
    secondaryBlue: '#205BF3',
    goldAccent: '#FED07B',
    text: '#0B1220',
    mutedText: '#4A5568',
    background: '#F6F8FC',
    white: '#FFFFFF',
    border: '#E2E8F0',
    lightBlue: '#EBF4FF'
  };

  // Core Preventive Services - 6 offerings for better grid
  const preventiveServices = [
    {
      icon: Search,
      title: 'Preventive Health Screenings',
      description: 'Early identification of potential health risks through routine evaluations and comprehensive diagnostic assessments.',
      highlight: 'Most Popular'
    },
    {
      icon: Briefcase,
      title: 'Work-Life Balance Guidance',
      description: 'Practical support for managing stress, fatigue, and professional demands to maintain sustainable health.',
      highlight: null
    },
    {
      icon: Heart,
      title: 'General Wellness Programs',
      description: 'Structured programs designed to sustain adult health, vitality, and long-term physical wellbeing.',
      highlight: null
    },
    {
      icon: BarChart3,
      title: 'Health Risk Assessments',
      description: 'Comprehensive assessments to identify, evaluate, and proactively manage future health concerns.',
      highlight: null
    },
    {
      icon: Dumbbell,
      title: 'Fitness & Nutrition Counseling',
      description: 'Personalized guidance to support physical fitness goals and establish balanced nutritional habits.',
      highlight: null
    },
    {
      icon: Activity,
      title: 'Chronic Care Prevention',
      description: 'Proactive strategies to prevent lifestyle-related conditions before they develop.',
      highlight: 'New'
    }
  ];

  // Who This Program Is For
  const targetAudience = [
    { icon: Briefcase, label: 'Working professionals', desc: 'Busy schedules need proactive health management' },
    { icon: UserPlus, label: 'Adults aged 30+', desc: 'Optimal time to establish preventive routines' },
    { icon: Activity, label: 'Lifestyle-risk individuals', desc: 'Managing sedentary or high-stress lifestyles' },
    { icon: Users, label: 'Family history concerns', desc: 'Proactive screening for hereditary conditions' },
    { icon: Shield, label: 'Health-conscious adults', desc: 'Committed to maintaining long-term wellness' }
  ];

  // How Preventive Care Works - 4 steps
  const preventiveCareSteps = [
    {
      number: '01',
      icon: ClipboardList,
      title: 'Initial Wellness Assessment',
      description: 'Comprehensive baseline evaluation of your current health status, lifestyle factors, and medical history.'
    },
    {
      number: '02',
      icon: FileText,
      title: 'Personalized Health Plan',
      description: 'Development of an individualized strategy addressing your specific health goals and risk factors.'
    },
    {
      number: '03',
      icon: UserCheck,
      title: 'Ongoing Guidance',
      description: 'Continuous professional support with regular check-ins to track progress and adjust recommendations.'
    },
    {
      number: '04',
      icon: Search,
      title: 'Periodic Screenings',
      description: 'Scheduled reassessments to ensure early detection and maintain optimal preventive outcomes.'
    }
  ];

  // Why Preventive Care Matters
  const whyPreventiveCare = [
    { icon: Shield, title: 'Reduce Health Risks', text: 'Prevention reduces long-term health risks by up to 70%' },
    { icon: Search, title: 'Early Detection', text: 'Catch potential issues before they become serious conditions' },
    { icon: TrendingUp, title: 'Sustained Productivity', text: 'Maintain peak performance through proactive health management' },
    { icon: Target, title: 'Informed Decisions', text: 'Empower yourself with data-driven health insights' }
  ];

  // GEWO Preventive Care Approach
  const gewoApproach = [
    { icon: BadgeCheck, text: 'Evidence-based recommendations' },
    { icon: UserCheck, text: 'Qualified healthcare professionals' },
    { icon: Lock, text: 'Secure and confidential consultations' },
    { icon: Target, text: 'Adult-focused preventive strategies' }
  ];

  // Trust Indicators
  const trustIndicators = [
    { icon: Award, label: 'Board Certified', value: 'Physicians' },
    { icon: Shield, label: 'HIPAA', value: 'Compliant' },
    { icon: Star, label: '4.9/5', value: 'Patient Rating' },
    { icon: Users, label: '10,000+', value: 'Adults Enrolled' }
  ];

  // FAQ Data
  const faqs = [
    {
      question: 'What screenings are included?',
      answer: 'Our preventive program includes a range of evidence-based screenings such as cardiovascular risk assessments, metabolic panels, blood pressure monitoring, cholesterol checks, and age-appropriate cancer screenings. The specific screenings recommended will depend on your individual risk factors and health history.'
    },
    {
      question: 'How often should preventive checks be done?',
      answer: 'For most adults, annual wellness evaluations are recommended. However, the frequency may vary based on your age, existing health conditions, family history, and personal risk factors. Your healthcare provider will recommend an appropriate schedule during your initial assessment.'
    },
    {
      question: 'Is this program for healthy adults?',
      answer: 'Yes, preventive care is particularly valuable for healthy adults who want to maintain their health and identify potential risks before they become serious issues. It is designed for anyone who wants to take a proactive approach to their long-term wellbeing.'
    },
    {
      question: 'Are consultations remote or in person?',
      answer: 'We offer both options. Many consultations, including initial assessments and follow-ups, can be conducted remotely via secure video. In-person visits are available when physical examinations or specific diagnostic procedures are required.'
    },
    {
      question: 'Can plans be personalized?',
      answer: 'Absolutely. Every preventive health plan is tailored to your individual needs, goals, lifestyle, and risk profile. We do not use one-size-fits-all approaches—your plan will be developed specifically for you.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes. All health information is protected under strict HIPAA-compliant security protocols. We use encrypted communication, secure data storage, and controlled access to ensure your personal health information remains confidential.'
    },
    {
      question: 'Is insurance required?',
      answer: 'Insurance is not required. While we accept many insurance plans, self-pay options are also available. Coverage details and costs will be discussed during your initial consultation with our enrollment team.'
    },
    {
      question: 'How do I enroll?',
      answer: 'Enrollment begins with scheduling a wellness consultation. During this session, we will review your health goals, discuss the program structure, and determine the best preventive care plan for your needs. You can schedule online or by contacting our care coordination team.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>GEWO Adult Care | Preventive Health & Wellness Programs</title>
        <meta name="description" content="GEWO Adult Care offers proactive screenings, wellness guidance, and personalized preventive programs designed to support long-term adult health. Enroll today." />
      </Helmet>

      <Navbar />

      {/* ========== HERO SECTION - ENHANCED ========== */}
      <section style={{
        background: `linear-gradient(135deg, ${colors.primaryBlue} 0%, ${colors.secondaryBlue} 100%)`,
        minHeight: isMobile ? 'auto' : '75vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '100px 20px 50px' : '120px 40px 80px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />

        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: isMobile ? '-10%' : '5%',
          width: isMobile ? '200px' : '350px',
          height: isMobile ? '200px' : '350px',
          background: `radial-gradient(circle, rgba(254, 208, 123, 0.1) 0%, transparent 70%)`,
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '5%',
          left: isMobile ? '-15%' : '3%',
          width: isMobile ? '180px' : '280px',
          height: isMobile ? '180px' : '280px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />

        <div style={{
          maxWidth: '1100px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          padding: '0 16px'
        }}>
          {/* Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(254, 208, 123, 0.15)',
            border: '1px solid rgba(254, 208, 123, 0.3)',
            borderRadius: '8px',
            padding: isMobile ? '8px 16px' : '10px 20px',
            marginBottom: isMobile ? '20px' : '28px',
            marginTop: '40px',
          }}>
            <Sparkles size={isMobile ? 16 : 18} color={colors.goldAccent} />
            <span style={{
              color: colors.goldAccent,
              fontSize: isMobile ? '11px' : '13px',
              fontWeight: '600',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>Preventive Health Program</span>
          </div>

          <h1 style={{
            fontSize: isMobile ? '28px' : isTablet ? '38px' : '48px',
            fontWeight: '700',
            color: colors.white,
            marginBottom: isMobile ? '16px' : '24px',
            lineHeight: '1.2',
            letterSpacing: '-0.5px'
          }}>
            Adult Care – Preventive{' '}
            <span style={{
              background: `linear-gradient(135deg, ${colors.goldAccent} 0%, #FFE4A8 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Health & Wellness
            </span>
          </h1>

          <p style={{
            fontSize: isMobile ? '15px' : '18px',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: isMobile ? '24px' : '32px',
            lineHeight: '1.7',
            maxWidth: '680px',
            margin: `0 auto ${isMobile ? '24px' : '32px'}`
          }}>
            Proactive screenings, wellness guidance, and personalized programs designed to support long-term adult health. Take control of your future today.
          </p>

          {/* Trust Indicators Row */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: isMobile ? '8px' : '16px',
            marginBottom: isMobile ? '24px' : '36px',
            flexWrap: 'wrap'
          }}>
            {trustIndicators.map((item, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: isMobile ? '8px 12px' : '10px 16px',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <item.icon size={isMobile ? 14 : 16} color={colors.goldAccent} />
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '700', color: colors.white }}>{item.value}</div>
                  <div style={{ fontSize: isMobile ? '9px' : '10px', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' }}>{item.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div style={{
            display: 'flex',
            gap: isMobile ? '12px' : '16px',
            justifyContent: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            marginBottom: isMobile ? '20px' : '28px'
          }}>
            <button style={{
              background: colors.goldAccent,
              color: colors.text,
              border: 'none',
              padding: isMobile ? '14px 28px' : '16px 36px',
              fontSize: isMobile ? '15px' : '16px',
              fontWeight: '600',
              borderRadius: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(254, 208, 123, 0.3)',
              transition: 'all 0.2s ease'
            }}>
              <Calendar size={18} />
              Enroll in Preventive Program
              <ArrowRight size={18} />
            </button>
            <button style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              color: colors.white,
              border: '1px solid rgba(255, 255, 255, 0.25)',
              padding: isMobile ? '14px 28px' : '16px 32px',
              fontSize: isMobile ? '15px' : '16px',
              fontWeight: '600',
              borderRadius: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}>
              <Phone size={18} />
              Schedule a Consultation
            </button>
          </div>

          {/* Support line */}
          <p style={{
            fontSize: isMobile ? '13px' : '14px',
            color: 'rgba(255, 255, 255, 0.7)',
            fontWeight: '500'
          }}>
            <CheckCircle size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
            Early detection. Informed decisions. Healthier outcomes.
          </p>
        </div>
      </section>

      {/* ========== CORE PREVENTIVE SERVICES ========== */}
      <section style={{
        padding: isMobile ? '50px 20px' : '90px 40px',
        background: colors.white
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '60px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: colors.lightBlue,
              padding: '8px 16px',
              borderRadius: '6px',
              marginBottom: '16px'
            }}>
              <Stethoscope size={16} color={colors.primaryBlue} />
              <span style={{ fontSize: '12px', fontWeight: '600', color: colors.primaryBlue, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Our Services</span>
            </div>
            <h2 style={{
              fontSize: isMobile ? '26px' : '38px',
              fontWeight: '700',
              color: colors.text,
              marginBottom: '14px',
              letterSpacing: '-0.3px'
            }}>
              Core Preventive Services
            </h2>
            <p style={{
              fontSize: isMobile ? '15px' : '17px',
              color: colors.mutedText,
              maxWidth: '550px',
              margin: '0 auto'
            }}>
              Comprehensive offerings to support proactive adult health management
            </p>
          </div>

          {/* Services Grid - 6 cards, 3x2 on desktop, 2x3 on tablet, 1x6 on mobile */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: isMobile ? '16px' : '24px'
          }}>
            {preventiveServices.map((service, index) => (
              <div key={index} style={{
                background: colors.white,
                border: `1px solid ${colors.border}`,
                borderRadius: '14px',
                padding: isMobile ? '24px 20px' : '32px 28px',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.primaryBlue;
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(26, 74, 197, 0.12)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.border;
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                {service.highlight && (
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: service.highlight === 'Most Popular' ? colors.primaryBlue : colors.goldAccent,
                    color: service.highlight === 'Most Popular' ? colors.white : colors.text,
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '700',
                    textTransform: 'uppercase'
                  }}>
                    {service.highlight}
                  </div>
                )}
                <div style={{
                  width: isMobile ? '48px' : '56px',
                  height: isMobile ? '48px' : '56px',
                  background: colors.lightBlue,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '18px'
                }}>
                  <service.icon size={isMobile ? 24 : 28} color={colors.primaryBlue} strokeWidth={1.5} />
                </div>
                <h3 style={{
                  fontSize: isMobile ? '17px' : '19px',
                  fontWeight: '600',
                  color: colors.text,
                  marginBottom: '12px',
                  lineHeight: '1.3'
                }}>
                  {service.title}
                </h3>
                <p style={{
                  fontSize: isMobile ? '14px' : '15px',
                  color: colors.mutedText,
                  lineHeight: '1.65',
                  margin: 0
                }}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WHO THIS PROGRAM IS FOR ========== */}
      <section style={{
        padding: isMobile ? '50px 20px' : '80px 40px',
        background: colors.background
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '48px' }}>
            <h2 style={{
              fontSize: isMobile ? '26px' : '38px',
              fontWeight: '700',
              color: colors.text,
              marginBottom: '14px'
            }}>
              Who This Program Is For
            </h2>
            <p style={{
              fontSize: isMobile ? '15px' : '17px',
              color: colors.mutedText
            }}>
              Preventive care designed for proactive adults
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: isMobile ? '12px' : '16px'
          }}>
            {targetAudience.map((item, index) => (
              <div key={index} style={{
                background: colors.white,
                border: `1px solid ${colors.border}`,
                borderRadius: '12px',
                padding: isMobile ? '20px' : '24px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.primaryBlue;
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(26, 74, 197, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.border;
                e.currentTarget.style.boxShadow = 'none';
              }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: colors.lightBlue,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <item.icon size={22} color={colors.primaryBlue} strokeWidth={1.5} />
                </div>
                <div>
                  <h4 style={{
                    fontSize: isMobile ? '16px' : '17px',
                    fontWeight: '600',
                    color: colors.text,
                    marginBottom: '4px'
                  }}>
                    {item.label}
                  </h4>
                  <p style={{
                    fontSize: isMobile ? '13px' : '14px',
                    color: colors.mutedText,
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== HOW PREVENTIVE CARE WORKS ========== */}
      <section style={{
        padding: isMobile ? '50px 20px' : '90px 40px',
        background: colors.white
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '60px' }}>
            <h2 style={{
              fontSize: isMobile ? '26px' : '38px',
              fontWeight: '700',
              color: colors.text,
              marginBottom: '14px'
            }}>
              How Preventive Care Works
            </h2>
            <p style={{
              fontSize: isMobile ? '15px' : '17px',
              color: colors.mutedText,
              maxWidth: '500px',
              margin: '0 auto'
            }}>
              A systematic approach to long-term health planning
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: isMobile ? '24px' : '20px',
            position: 'relative'
          }}>
            {/* Connection Line (desktop only) */}
            {!isMobile && (
              <div style={{
                position: 'absolute',
                top: '50px',
                left: '12%',
                right: '12%',
                height: '3px',
                background: `linear-gradient(90deg, ${colors.primaryBlue}, ${colors.goldAccent}, ${colors.primaryBlue})`,
                zIndex: 0,
                borderRadius: '2px'
              }} />
            )}

            {preventiveCareSteps.map((step, index) => (
              <div key={index} style={{
                position: 'relative',
                textAlign: 'center',
                zIndex: 1
              }}>
                {/* Step Circle */}
                <div style={{
                  width: isMobile ? '80px' : '100px',
                  height: isMobile ? '80px' : '100px',
                  background: `linear-gradient(135deg, ${colors.primaryBlue}, ${colors.secondaryBlue})`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  boxShadow: '0 8px 30px rgba(26, 74, 197, 0.25)',
                  position: 'relative'
                }}>
                  <step.icon size={isMobile ? 32 : 40} color={colors.white} strokeWidth={1.5} />
                  {/* Step Number Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    width: '32px',
                    height: '32px',
                    background: colors.goldAccent,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    fontWeight: '700',
                    color: colors.text,
                    boxShadow: '0 4px 12px rgba(254, 208, 123, 0.4)'
                  }}>
                    {step.number}
                  </div>
                </div>

                <h3 style={{
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: '600',
                  color: colors.text,
                  marginBottom: '10px'
                }}>
                  {step.title}
                </h3>
                <p style={{
                  fontSize: isMobile ? '13px' : '14px',
                  color: colors.mutedText,
                  lineHeight: '1.6',
                  maxWidth: '240px',
                  margin: '0 auto'
                }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WHY PREVENTIVE CARE MATTERS ========== */}
      <section style={{
        padding: isMobile ? '50px 20px' : '80px 40px',
        background: colors.background
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '48px' }}>
            <h2 style={{
              fontSize: isMobile ? '26px' : '38px',
              fontWeight: '700',
              color: colors.text,
              marginBottom: '14px'
            }}>
              Why Preventive Care Matters
            </h2>
            <p style={{
              fontSize: isMobile ? '15px' : '17px',
              color: colors.mutedText
            }}>
              The value of proactive health management
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '20px'
          }}>
            {whyPreventiveCare.map((item, index) => (
              <div key={index} style={{
                background: colors.white,
                border: `1px solid ${colors.border}`,
                borderRadius: '14px',
                padding: isMobile ? '24px' : '28px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '18px'
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: `linear-gradient(135deg, ${colors.primaryBlue}, ${colors.secondaryBlue})`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <item.icon size={26} color={colors.white} strokeWidth={1.5} />
                </div>
                <div>
                  <h4 style={{
                    fontSize: isMobile ? '17px' : '18px',
                    fontWeight: '600',
                    color: colors.text,
                    marginBottom: '6px'
                  }}>
                    {item.title}
                  </h4>
                  <p style={{
                    fontSize: isMobile ? '14px' : '15px',
                    color: colors.mutedText,
                    margin: 0,
                    lineHeight: '1.6'
                  }}>
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== GEWO PREVENTIVE CARE APPROACH ========== */}
      <section style={{
        padding: isMobile ? '50px 20px' : '80px 40px',
        background: `linear-gradient(135deg, ${colors.primaryBlue}, ${colors.secondaryBlue})`
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '48px' }}>
            <h2 style={{
              fontSize: isMobile ? '26px' : '38px',
              fontWeight: '700',
              color: colors.white,
              marginBottom: '14px'
            }}>
              The GEWO Approach
            </h2>
            <p style={{
              fontSize: isMobile ? '15px' : '17px',
              color: 'rgba(255, 255, 255, 0.85)'
            }}>
              Your long-term preventive health partner
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '14px'
          }}>
            {gewoApproach.map((item, index) => (
              <div key={index} style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                padding: isMobile ? '18px 20px' : '22px 26px',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  background: colors.goldAccent,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <item.icon size={22} color={colors.text} strokeWidth={1.5} />
                </div>
                <p style={{
                  fontSize: isMobile ? '15px' : '16px',
                  fontWeight: '500',
                  color: colors.white,
                  margin: 0
                }}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== COVERAGE & ACCESS ========== */}
      <section style={{
        padding: isMobile ? '50px 20px' : '80px 40px',
        background: colors.white
      }}>
        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: colors.lightBlue,
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <FileText size={30} color={colors.primaryBlue} strokeWidth={1.5} />
          </div>
          <h2 style={{
            fontSize: isMobile ? '26px' : '36px',
            fontWeight: '700',
            color: colors.text,
            marginBottom: '18px'
          }}>
            Coverage & Access
          </h2>
          <p style={{
            fontSize: isMobile ? '15px' : '16px',
            color: colors.mutedText,
            lineHeight: '1.8',
            marginBottom: '10px'
          }}>
            Insurance acceptance varies by plan and provider. Self-pay options are available for those without applicable coverage.
          </p>
          <p style={{
            fontSize: isMobile ? '15px' : '16px',
            color: colors.mutedText,
            lineHeight: '1.8',
            marginBottom: '28px'
          }}>
            Specific details regarding coverage, costs, and program structure will be discussed during your initial consultation.
          </p>
          <button style={{
            background: colors.lightBlue,
            color: colors.primaryBlue,
            border: `1px solid ${colors.border}`,
            padding: isMobile ? '14px 24px' : '14px 28px',
            fontSize: '15px',
            fontWeight: '600',
            borderRadius: '10px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = colors.primaryBlue;
            e.currentTarget.style.color = colors.white;
            e.currentTarget.style.borderColor = colors.primaryBlue;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = colors.lightBlue;
            e.currentTarget.style.color = colors.primaryBlue;
            e.currentTarget.style.borderColor = colors.border;
          }}
          >
            <Phone size={18} />
            Contact Enrollment Team
          </button>
        </div>
      </section>

      {/* ========== FAQ SECTION ========== */}
      <section style={{
        padding: isMobile ? '50px 20px' : '90px 40px',
        background: colors.background
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '48px' }}>
            <h2 style={{
              fontSize: isMobile ? '26px' : '38px',
              fontWeight: '700',
              color: colors.text,
              marginBottom: '14px'
            }}>
              Frequently Asked Questions
            </h2>
            <p style={{
              fontSize: isMobile ? '15px' : '17px',
              color: colors.mutedText
            }}>
              Common questions about preventive health programs
            </p>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {faqs.map((faq, index) => (
              <div 
                key={index}
                style={{
                  background: colors.white,
                  border: `1px solid ${openFaq === index ? colors.primaryBlue : colors.border}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  boxShadow: openFaq === index ? '0 4px 20px rgba(26, 74, 197, 0.1)' : 'none'
                }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  style={{
                    width: '100%',
                    padding: isMobile ? '18px 20px' : '20px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <span style={{
                    fontSize: isMobile ? '15px' : '16px',
                    fontWeight: '600',
                    color: openFaq === index ? colors.primaryBlue : colors.text,
                    paddingRight: '16px'
                  }}>
                    {faq.question}
                  </span>
                  <div style={{
                    width: '34px',
                    height: '34px',
                    background: openFaq === index ? colors.primaryBlue : colors.lightBlue,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.2s ease'
                  }}>
                    {openFaq === index ? (
                      <ChevronUp size={18} color={colors.white} />
                    ) : (
                      <ChevronDown size={18} color={colors.primaryBlue} />
                    )}
                  </div>
                </button>
                <div style={{
                  maxHeight: openFaq === index ? '500px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease'
                }}>
                  <div style={{
                    padding: isMobile ? '0 20px 20px' : '0 24px 24px',
                    fontSize: isMobile ? '14px' : '15px',
                    color: colors.mutedText,
                    lineHeight: '1.7'
                  }}>
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section style={{
        padding: isMobile ? '60px 20px' : '100px 40px',
        background: `linear-gradient(135deg, ${colors.primaryBlue}, ${colors.secondaryBlue})`,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Element */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '500px',
          height: '500px',
          background: `radial-gradient(circle, rgba(254, 208, 123, 0.1) 0%, transparent 70%)`,
          borderRadius: '50%'
        }} />

        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(254, 208, 123, 0.2)',
            padding: '8px 16px',
            borderRadius: '6px',
            marginBottom: '24px'
          }}>
            <Heart size={16} color={colors.goldAccent} />
            <span style={{ fontSize: '12px', fontWeight: '600', color: colors.goldAccent, textTransform: 'uppercase' }}>Start Today</span>
          </div>

          <h2 style={{
            fontSize: isMobile ? '28px' : '42px',
            fontWeight: '700',
            color: colors.white,
            marginBottom: '18px',
            lineHeight: '1.2'
          }}>
            Your health, planned for the future.
          </h2>
          <p style={{
            fontSize: isMobile ? '16px' : '18px',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '36px',
            maxWidth: '550px',
            margin: '0 auto 36px',
            lineHeight: '1.7'
          }}>
            Take proactive steps toward long-term wellness with a personalized preventive health plan designed just for you.
          </p>

          <div style={{
            display: 'flex',
            gap: isMobile ? '12px' : '16px',
            justifyContent: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center'
          }}>
            <button style={{
              background: colors.goldAccent,
              color: colors.text,
              border: 'none',
              padding: isMobile ? '16px 32px' : '18px 40px',
              fontSize: isMobile ? '15px' : '17px',
              fontWeight: '600',
              borderRadius: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center',
              boxShadow: '0 4px 24px rgba(254, 208, 123, 0.4)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(254, 208, 123, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 24px rgba(254, 208, 123, 0.4)';
            }}
            >
              <Calendar size={18} />
              Enroll in Preventive Program
            </button>
            <button style={{
              background: 'transparent',
              color: colors.white,
              border: '2px solid rgba(255, 255, 255, 0.35)',
              padding: isMobile ? '14px 28px' : '16px 32px',
              fontSize: isMobile ? '15px' : '17px',
              fontWeight: '600',
              borderRadius: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.35)';
            }}
            >
              <Phone size={18} />
              Book a Wellness Consultation
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default GeriatricAdultCare;
